import { useRef, useImperativeHandle, forwardRef, useState, useEffect } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import SignatureCanvas from "react-signature-canvas";
import { Button } from "@/components/ui/button";
import { Eraser, PenLine, Maximize2, Check, X } from "lucide-react";

const SignaturePad = forwardRef(({ label = "Firma qui", onChange }, ref) => {
  const [value, setValue] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const fsPadRef = useRef(null);
  const fsAreaRef = useRef(null);

  const report = (v) => { setValue(v); onChange?.(!!v); };

  // Size the full-screen canvas to fill its container once the overlay opens.
  // The pen (tavoletta grafica) is mapped to the whole screen, so signing happens
  // over the entire drawing area and the cursor cannot "leave" the signature box.
  useEffect(() => {
    if (!fullscreen) return;
    const size = () => {
      const pad = fsPadRef.current;
      const area = fsAreaRef.current;
      if (!pad || !area) return;
      const canvas = pad.getCanvas();
      const w = area.clientWidth;
      const h = area.clientHeight;
      if (!w || !h) return;
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      canvas.width = w * ratio;
      canvas.height = h * ratio;
      canvas.getContext("2d", { willReadFrequently: true }).scale(ratio, ratio);
      pad.clear();
    };
    const id = requestAnimationFrame(size);
    const t = setTimeout(size, 80);
    window.addEventListener("resize", size);
    return () => {
      cancelAnimationFrame(id);
      clearTimeout(t);
      window.removeEventListener("resize", size);
    };
  }, [fullscreen]);

  // Crop the drawn canvas to the bounding box of the ink so the PDF stores only
  // the signature (no surrounding whitespace), otherwise preserveAspectRatio would
  // shrink the stroke. Scans alpha channel to find bounds, adds small padding.
  const trimToInk = (canvas) => {
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    const { width, height } = canvas;
    const data = ctx.getImageData(0, 0, width, height).data;
    let minX = width, minY = height, maxX = 0, maxY = 0, found = false;
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (data[(y * width + x) * 4 + 3] > 10) {
          found = true;
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (!found) return null;
    const pad = Math.round(Math.max(width, height) * 0.01) + 10;
    minX = Math.max(0, minX - pad);
    minY = Math.max(0, minY - pad);
    maxX = Math.min(width - 1, maxX + pad);
    maxY = Math.min(height - 1, maxY + pad);
    const cw = maxX - minX + 1;
    const ch = maxY - minY + 1;
    const out = document.createElement("canvas");
    out.width = cw;
    out.height = ch;
    out.getContext("2d").drawImage(canvas, minX, minY, cw, ch, 0, 0, cw, ch);
    return out.toDataURL("image/png");
  };

  const confirmFs = () => {
    const pad = fsPadRef.current;
    if (!pad || pad.isEmpty()) { setFullscreen(false); return; }
    const trimmed = trimToInk(pad.getCanvas());
    report(trimmed || pad.getCanvas().toDataURL("image/png"));
    setFullscreen(false);
  };

  useImperativeHandle(ref, () => ({
    isEmpty: () => !value,
    getData: () => value,
    clear: () => report(null),
  }));

  return (
    <div className="space-y-2">
      <div
        className="relative border-2 border-dashed border-border rounded-md bg-secondary/40 overflow-hidden cursor-pointer"
        style={{ height: 220 }}
        onClick={() => setFullscreen(true)}
        data-testid="signature-open-area"
      >
        {value ? (
          <img
            src={value}
            alt="Firma"
            className="absolute inset-0 w-full h-full object-contain p-2"
            data-testid="signature-preview"
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none text-muted-foreground/60">
            <PenLine className="h-7 w-7 mb-1" />
            <span className="label-caps">{label}</span>
            <span className="text-xs mt-1.5 flex items-center gap-1">
              <Maximize2 className="h-3 w-3" /> Tocca per firmare a schermo intero
            </span>
          </div>
        )}
      </div>
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="signature-open-button"
          onClick={() => setFullscreen(true)}
        >
          <PenLine className="h-4 w-4 mr-1.5" /> {value ? "Rifirma" : "Firma a schermo intero"}
        </Button>
        {value && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="signature-clear-button"
            onClick={() => report(null)}
          >
            <Eraser className="h-4 w-4 mr-1.5" /> Cancella firma
          </Button>
        )}
      </div>

      <DialogPrimitive.Root open={fullscreen} onOpenChange={setFullscreen}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 bg-white" style={{ zIndex: 2147483000 }} />
          <DialogPrimitive.Content
            className="fixed inset-0 flex flex-col bg-white focus:outline-none"
            style={{ zIndex: 2147483001 }}
            data-testid="signature-fullscreen"
            onOpenAutoFocus={(e) => e.preventDefault()}
            aria-describedby={undefined}
          >
            <DialogPrimitive.Title className="sr-only">{label}</DialogPrimitive.Title>
            <div className="flex items-center justify-between gap-2 px-4 py-3 border-b bg-secondary/40 shrink-0">
              <span className="label-caps text-sm truncate">{label} — firma in tutta l'area</span>
              <div className="flex gap-2 shrink-0">
                <Button type="button" variant="outline" size="sm" data-testid="fs-clear-button" onClick={() => fsPadRef.current?.clear()}>
                  <Eraser className="h-4 w-4 mr-1.5" /> Cancella
                </Button>
                <Button type="button" variant="outline" size="sm" data-testid="fs-cancel-button" onClick={() => setFullscreen(false)}>
                  <X className="h-4 w-4 mr-1.5" /> Annulla
                </Button>
                <Button type="button" size="sm" data-testid="fs-confirm-button" onClick={confirmFs}>
                  <Check className="h-4 w-4 mr-1.5" /> Conferma firma
                </Button>
              </div>
            </div>
            <div ref={fsAreaRef} className="flex-1 relative">
              <SignatureCanvas
                ref={fsPadRef}
                penColor="#111827"
                canvasProps={{ className: "sig-canvas", "data-testid": "signature-canvas" }}
              />
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </div>
  );
});

SignaturePad.displayName = "SignaturePad";
export default SignaturePad;
